<template>
    <div class="max-w-2xl mx-auto p-4">
        <h1 class="text-2xl font-semibold text-theme-base mb-2">
            Tiempos de entrega
        </h1>
        <p class="text-sm text-theme-muted mb-6">
            Estos valores alimentan el algoritmo que calcula los días de
            preparación y entrega de una orden.
        </p>

        <div class="bg-theme-card rounded-xl p-6 flex flex-col gap-8">
            <section>
                <h2 class="text-lg font-medium text-theme-base mb-4">
                    Tiempos de preparación
                </h2>
                <div class="flex flex-wrap gap-4">
                    <div class="w-40">
                        <custom-input
                            v-model.number="settings.prepareTime.min"
                            type="number"
                            label="Mínimo (min)"
                            :min="0"
                            class="w-full"
                        />
                    </div>
                    <div class="w-40">
                        <custom-input
                            v-model.number="settings.prepareTime.max"
                            type="number"
                            label="Máximo (min)"
                            :min="0"
                            class="w-full"
                        />
                    </div>
                </div>
            </section>

            <section>
                <h2 class="text-lg font-medium text-theme-base mb-4">
                    Días de operación
                </h2>
                <div class="flex flex-col gap-4">
                    <div>
                        <label class="text-sm text-theme-muted block mb-2">
                            Días de entrega
                        </label>
                        <weekday-selector v-model="settings.deliveryWeekdays" />
                    </div>
                    <div>
                        <label class="text-sm text-theme-muted block mb-2">
                            Días de empaque
                        </label>
                        <weekday-selector v-model="settings.packageWeekdays" />
                    </div>
                </div>
            </section>

            <section>
                <h2 class="text-lg font-medium text-theme-base mb-4">
                    Horas de corte y anticipación
                </h2>
                <div class="flex flex-wrap gap-4">
                    <div class="w-48">
                        <custom-input
                            v-model="settings.thresholdHour"
                            type="time"
                            label="Hora de corte de entrega"
                            class="w-full"
                        />
                    </div>
                    <div class="w-48">
                        <custom-input
                            v-model="settings.packageThresholdHour"
                            type="time"
                            label="Hora de corte de empaque"
                            class="w-full"
                        />
                    </div>
                    <div class="w-48">
                        <custom-input
                            v-model.number="settings.offsetPackageHours"
                            type="number"
                            label="Anticipación empaque (h)"
                            :min="0"
                            class="w-full"
                        />
                    </div>
                    <div class="w-48">
                        <custom-input
                            v-model.number="settings.offsetDeliveryHours"
                            type="number"
                            label="Anticipación entrega (h)"
                            :min="0"
                            class="w-full"
                        />
                    </div>
                    <div class="w-48">
                        <custom-input
                            v-model.number="
                                settings.unknownCitiesOffsetDeliveryHours
                            "
                            type="number"
                            label="Anticipación ciudades nuevas (h)"
                            :min="0"
                            class="w-full"
                        />
                    </div>
                </div>
            </section>

            <div class="flex justify-end pt-2">
                <app-button :loading="isLoading" @click="save">
                    Actualizar
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import {
    defaultOrdersSettings,
    mapOrdersSettingsFromApi,
    mapOrdersSettingsToApi
} from "@/common/fields/orders-settings";

import CustomInput from "@/components/utils/CustomInput.vue";
import WeekdaySelector from "@/components/utils/WeekdaySelector.vue";

export default {
    name: "DeliveryScheduleSettingsPage",
    components: { WeekdaySelector, CustomInput },
    layout: "app",
    data: () => ({
        isLoading: false,
        settings: defaultOrdersSettings()
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"])
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if (
            !(await verifyPagePermission(
                "tenant:GetOrdersSettings",
                "/app/settings"
            ))
        )
            return;
        await this.loadSettings();
    },
    methods: {
        ...mapActions("tenants", [
            "getTenantOrdersSettings",
            "updateTenantOrdersSettings"
        ]),
        async loadSettings() {
            this.$loading.show();
            try {
                const data = await this.getTenantOrdersSettings();
                this.settings = mapOrdersSettingsFromApi(data);
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async save() {
            this.$loading.show();
            this.isLoading = true;
            try {
                await this.updateTenantOrdersSettings(
                    mapOrdersSettingsToApi(this.settings)
                );
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        }
    }
};
</script>
